/* eslint-disable @typescript-eslint/consistent-type-imports */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable new-cap */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {IConvesioPayContext, PaymentResult, ConvesioPayPaymentRequest, ConvesioPaySubscriptionRequest, ConvesioPayRecurringRequest} from '../../models/convesiopay';
import {registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {getLocaleText} from '../../util/translation';
import {DefaultCart} from '../../reducers/cartReducer';
import {FeatureFlag} from '../../../../@type/features';
import {Feature} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {setupConvesioPayButton} from './button';
import {displayPaymentErrorMessage, type OrderService, requestCartCalculation} from '../order';
import {startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {getErrorMessage} from '../../../../@shared/ts/error';
import {stripHtml, $qsAll} from '../../../../@shared/ts/dom';
import {requiredPolicyErrorMessage, validateRequiredPolicyCheckboxes} from '../../features/policyCheckboxes';
import {
	clearConvesioPayCardToken,
	ensureConvesioPayCardToken,
	isConvesioPayCardTokenRequiredForSubmit,
	updateConvesioPayCardStateFromChange,
} from './card-token';
// Using generic card badge for now - can be replaced with ConvesioPay specific logo later
import cardBadgeURL from '../../../img/badge/card.svg';

declare global {
	interface Window {
		ConvesioPay: any;
	}
}

class ConvesioPayGateway {
	private cpay: any;
	private component: any;

	async initialize(config: IConvesioPayContext['config']): Promise<void> {
		// Load ConvesioPay script dynamically (like GiveWP)
		await this.loadConvesioPayScript();

		// Initialize ConvesioPay (like GiveWP)
		this.cpay = window.ConvesioPay(config.apiKey);
		this.component = this.cpay.component({
			environment: config.environment,
			clientKey: config.clientKey,
		});
	}

	async loadConvesioPayScript(): Promise<void> {
		return new Promise((resolve, reject) => {
			// Check if script already exists (like GiveWP)
			const existingScript = document.querySelector('script[src="https://js.convesiopay.com/v1/"]');
			if (existingScript) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.src = 'https://js.convesiopay.com/v1/';
			script.async = true;
			script.onload = () => {
				resolve();
			};

			script.onerror = () => {
				reject(new Error('Failed to load ConvesioPay script'));
			};

			document.head.appendChild(script);
		});
	}

	async mountPaymentForm(selector: string): Promise<void> {
		if (!this.component) {
			throw new Error('ConvesioPay component not initialized');
		}

		// Mount component (like GiveWP)
		this.component.mount(selector);

		// Set up change listener (like GiveWP)
		this.component.on('change', (event: any) => {
			updateConvesioPayCardStateFromChange(event);
		});
	}

	async createToken(): Promise<string> {
		if (!this.component) {
			throw new Error('ConvesioPay component not initialized');
		}

		return ensureConvesioPayCardToken(this.component);
	}

	async createPayment(request: ConvesioPayPaymentRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch('/api/v1/convesiopay/payments', {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify(request),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Payment failed',
			};
		}
	}

	async createSubscriptionPayment(request: ConvesioPaySubscriptionRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch('/api/v1/convesiopay/subscriptions/initial', {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify(request),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Subscription payment failed',
			};
		}
	}

	async processRecurringPayment(request: ConvesioPayRecurringRequest): Promise<PaymentResult> {
		try {
			// Direct API call like GiveWP
			const response = await fetch(`/api/v1/convesiopay/subscriptions/${request.customerId}/process`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({
					customerId: request.customerId,
					amount: request.amount,
					orderDetails: request.orderDetails,
				}),
			});

			const result = await response.json();

			if (result.success) {
				return {
					success: true,
					paymentId: result.data.id,
					status: result.data.status,
				};
			}

			return {
				success: false,
				error: result.error,
			};
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Recurring payment failed',
			};
		}
	}
}

export const convesioPayGateway = new ConvesioPayGateway();
let isConvesioPayGatewayMountSubscriptionRegistered = false;

const CONVESIOPAY_GPAY_AUTO_SUBMIT_COOLDOWN_MS = 1200;
const CONVESIOPAY_GPAY_FINAL_SUBMIT_DUPLICATE_TOKEN_LOCK_MS = 5000;
const CONVESIOPAY_REDIRECT_FALLBACK_RESET_MS = 4000;

function getConvesiopayGooglePayTokenFromEvent(event: any): string | null {
	if (!event || typeof event !== 'object') {
		return null;
	}

	const candidateTokens: unknown[] = [
		event?.token,
		event?.paymentData?.token,
		event?.data?.token,
		event?.paymentData?.tokenizationData?.token,
		event?.data?.tokenizationData?.token,
		event?.tokenizationData?.token,
		event?.tokenizationData?.tokenizationData?.token,
		event?.paymentData?.paymentMethodData?.tokenizationData?.token,
		event?.data?.paymentMethodData?.tokenizationData?.token,
		event?.token?.id,
		event?.paymentData?.token?.id,
		event?.data?.token?.id,
		event?.token?.token,
		event?.paymentData?.token?.token,
		event?.data?.token?.token,
		event?.paymentData?.paymentMethodData?.token?.id,
		event?.data?.paymentMethodData?.token?.id,
	];

	for (const candidate of candidateTokens) {
		if (typeof candidate === 'string') {
			const tokenValue = candidate.trim();
			if (tokenValue.length > 0) {
				return tokenValue;
			}
		}
	}

	return null;
}

function getConvesioPayPaymentDataToken(paymentData: unknown): string | null {
	if (!paymentData || typeof paymentData !== 'object') {
		return null;
	}

	const {token} = paymentData as {token?: unknown};
	if (typeof token !== 'string') {
		return null;
	}

	const trimmedToken = token.trim();
	return trimmedToken.length > 0 ? trimmedToken : null;
}

// Module-level mount tracking variables
let isConvesioPayComponentMounted = false;
let mountedConvesioPayComponent: any = null;
let mountedComponentTarget: string | null = null;
let googlePaySessionCreationPromise: Promise<void> | null = null;
let googlePaySessionSignature = '';
let googlePaySessionPrimed = false;
let googlePaySessionRetryLockUntilMs = 0;
let isConvesioPayGooglePayAutoSubmitInProgress = false;
let isConvesioPayOrderSubmissionInProgress = false;
let lastConvesioPayGooglePayAutoSubmitTs = 0;
let lastGooglePayFinalSubmitToken: string | null = null;
let lastGooglePayFinalSubmitTs = 0;
let convesioPayFinalSubmitHandler: (() => Promise<void>) | null = null;
let googlePayPreAuthorizationPromise: Promise<void> | null = null;
let isConvesioPayGooglePayAuthorizationInProgress = false;
let clearGooglePayAuthorizationFocusReset: (() => void) | null = null;
let applePaySessionAmountCents: number | null = null;

const CONVESIOPAY_GOOGLE_PAY_ERROR_SELECTOR = '.pp-pm-error-text[data-convesiopay-google-pay-error="true"]';

// Track current ConvesioPay internal method for fee calculation
let currentConvesioPayMethod: ConvesioPayMethod | undefined;

type ConvesioPayMethod = 'card' | 'applepay' | 'btcpay' | 'googlepay';

/**
 * Fee configuration type
 */
interface FeeMethodConfig {
	enabled: boolean;
	type: string;
	amount: number;
	label: string;
}

interface FeeConfig {
	card?: FeeMethodConfig;
	applepay?: FeeMethodConfig;
	googlepay?: FeeMethodConfig;
	btcpay?: FeeMethodConfig;
}

function normalizeConvesioPayMethod(method: unknown): ConvesioPayMethod | undefined {
	if (typeof method !== 'string') {
		return undefined;
	}

	const normalizedMethod = method.toLowerCase().trim();
	if (normalizedMethod === 'applepay' || normalizedMethod === 'apple-pay' || normalizedMethod.includes('applepay')) {
		return 'applepay';
	}

	if (normalizedMethod === 'googlepay' || normalizedMethod === 'google-pay' || normalizedMethod.includes('googlepay') || normalizedMethod.includes('gpay')) {
		return 'googlepay';
	}

	if (normalizedMethod === 'btcpay' || normalizedMethod === 'crypto' || normalizedMethod.includes('btcpay') || normalizedMethod.includes('bitcoin')) {
		return 'btcpay';
	}

	if (normalizedMethod === 'card' || normalizedMethod.includes('credit') || normalizedMethod.includes('debit')) {
		return 'card';
	}

	return undefined;
}

function getConvesioPayConfigFromMetadata(): IConvesioPayContext['config'] | null {
	const apiKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_key');
	const clientKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'client_key');
	const environment = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'environment');
	const baseURL = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_url');

	if (!apiKey || !clientKey) {
		return null;
	}

	const normalizedEnvironment = environment === 'test' || environment === 'live' ? environment : 'live';

	return {
		apiKey,
		clientKey,
		environment: normalizedEnvironment,
		baseURL: baseURL ?? 'https://api.convesiopay.com',
	};
}

function getGooglePayFinalSubmitTokenSnapshot(): {token: string | null} {
	const paymentToken = (window as any).convesiopayPaymentToken;
	if (typeof paymentToken === 'string' && paymentToken.trim() !== '') {
		return {token: paymentToken};
	}

	const googlePayPaymentData = (window as any).convesiopayGooglePayPaymentData;
	const googlePayToken = googlePayPaymentData?.token;
	if (typeof googlePayToken === 'string' && googlePayToken.trim() !== '') {
		return {token: googlePayToken};
	}

	return {token: null};
}

function getGooglePaySessionAmountCents(): number | null {
	const [amount] = googlePaySessionSignature.split('-');
	const parsedAmount = Number(amount);
	return Number.isFinite(parsedAmount) && parsedAmount > 0 ? Math.round(parsedAmount) : null;
}

function getApplePaySessionAmountCents(): number | null {
	const windowAmount = (window as any).convesiopayApplePayAuthorizedAmount;
	if (typeof windowAmount === 'number' && Number.isFinite(windowAmount) && windowAmount > 0) {
		return Math.round(windowAmount);
	}

	if (typeof windowAmount === 'string' && windowAmount.trim() !== '') {
		const parsedAmount = Number(windowAmount);
		return Number.isFinite(parsedAmount) && parsedAmount > 0 ? Math.round(parsedAmount) : null;
	}

	return applePaySessionAmountCents;
}

function setApplePaySessionAmountCents(amount: number): void {
	applePaySessionAmountCents = Math.round(amount);
	(window as any).convesiopayApplePayAuthorizedAmount = applePaySessionAmountCents;
}

function getConvesioPayGooglePayAuthorizedAmountCents(paymentData: unknown): number | null {
	if (!paymentData || typeof paymentData !== 'object') {
		return null;
	}

	const {amount} = paymentData as {amount?: unknown};
	if (typeof amount === 'number' && Number.isFinite(amount) && amount > 0) {
		return Math.round(amount);
	}

	if (typeof amount === 'string' && amount.trim() !== '') {
		const parsedAmount = Number(amount);
		return Number.isFinite(parsedAmount) && parsedAmount > 0 ? Math.round(parsedAmount) : null;
	}

	return null;
}

const getConvesioPayApplePayAuthorizedAmountCents = getConvesioPayGooglePayAuthorizedAmountCents;

function getConvesioPayGooglePayFinalAmountCents(): number {
	return Math.round(DefaultCart.total() * 100);
}

function isConvesioPayGooglePayTerminalEvent(event: any): boolean {
	const status = String(event?.status ?? event?.paymentData?.status ?? event?.data?.status ?? '').toLowerCase();
	return Boolean(event?.errors?.length || event?.error) || ['cancelled', 'canceled', 'failed', 'failure', 'error'].includes(status);
}

function isConvesioPayGooglePayPaymentDataStale(paymentData: unknown): boolean {
	const authorizedAmount = getConvesioPayGooglePayAuthorizedAmountCents(paymentData);
	if (authorizedAmount === null) {
		if (!paymentData || typeof paymentData !== 'object') {
			return false;
		}

		const paymentRecord = paymentData as {hasToken?: unknown};
		return getConvesioPayPaymentDataToken(paymentData) !== null || paymentRecord.hasToken === true;
	}

	const finalAmount = getConvesioPayGooglePayFinalAmountCents();
	return finalAmount > 0 && authorizedAmount !== finalAmount;
}

const isConvesioPayApplePayPaymentDataStale = isConvesioPayGooglePayPaymentDataStale;

function clearConvesioPayApplePayPaymentState(): void {
	const applePayPaymentData = (window as any).convesiopayApplePayPaymentData;
	const applePayToken = getConvesioPayPaymentDataToken(applePayPaymentData);
	if (applePayToken && (window as any).convesiopayPaymentToken === applePayToken) {
		delete (window as any).convesiopayPaymentToken;
	}

	delete (window as any).convesiopayApplePayPaymentData;
	delete (window as any).convesiopayApplePayAuthorizedAmount;
	applePaySessionAmountCents = null;
}

async function invalidateStaleConvesioPayApplePayPaymentData(message?: string): Promise<void> {
	clearConvesioPayApplePayPaymentState();
	if (message) {
		displayPaymentErrorMessage(message);
	}

	await recreateApplePaySessionWithCorrectAmount().catch(() => undefined);
	stopConvesioPayProcessingAfterFailure();
}

async function invalidateStaleConvesioPayGooglePayPaymentData(message?: string, recreateSession = true): Promise<void> {
	clearConvesioPayGooglePayPaymentState();
	if (message) {
		displayConvesioPayGooglePayErrorMessage(message);
	}

	if (recreateSession) {
		await recreateGooglePaySessionWithCorrectAmount().catch(() => undefined);
	}

	stopConvesioPayProcessingAfterFailure();
}

function isGooglePayMethodEnabled(): boolean {
	const rawMethods = Feature.metadata<unknown>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'active_methods') ?? ((window as any).peachpay_convesiopay_unified_data?.active_methods ?? []);
	const methods = Array.isArray(rawMethods) ? rawMethods : [];
	return methods.some(method => {
		if (typeof method !== 'string') {
			return false;
		}

		return normalizeConvesioPayMethod(method) === 'googlepay';
	});
}

function isBtcpayMethodEnabled(): boolean {
	const rawMethods = Feature.metadata<unknown>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'active_methods') ?? ((window as any).peachpay_convesiopay_unified_data?.active_methods ?? []);
	const methods = Array.isArray(rawMethods) ? rawMethods : [];
	return methods.some(method => {
		if (typeof method !== 'string') {
			return false;
		}

		return normalizeConvesioPayMethod(method) === 'btcpay';
	});
}

function getGooglePaySessionSignature(orderData: Awaited<ReturnType<typeof getOrderDataForConvesioPay>>): string {
	return `${orderData.amount}-${orderData.currency}`;
}

function hasGooglePayButtonElement(): boolean {
	return document.querySelector('.vgs-google-pay, #gpay-button-online-api-id') !== null;
}

function isGooglePaySessionRecoverableError(error: unknown): boolean {
	const message = String((error as {message?: unknown})?.message || error || '').toLowerCase();

	if (hasGooglePayButtonElement()) {
		return true;
	}

	return message.includes('already') && (
		message.includes('initialized')
		|| message.includes('initialized error')
		|| message.includes('already exists')
		|| message.includes('already created')
		|| message.includes('already initialized')
		|| message.includes('already opened')
		|| message.includes('already exists')
		|| message.includes('already been created')
		|| message.includes('already in use')
		|| message.includes('already active')
		|| message.includes('button already')
	);
}

function isGooglePaySessionNotReadyError(error: unknown): boolean {
	const message = String((error as {message?: unknown})?.message || error || '').toLowerCase();
	return (
		message.includes('creategooglepaysession')
		|| message.includes('not initialized')
		|| message.includes('not available')
		|| message.includes('required to use this method')
	);
}

function isGooglePayFinalSubmitDuplicate(token: string | null): boolean {
	if (!token) {
		return false;
	}

	return token === lastGooglePayFinalSubmitToken
		&& Date.now() - lastGooglePayFinalSubmitTs < CONVESIOPAY_GPAY_FINAL_SUBMIT_DUPLICATE_TOKEN_LOCK_MS;
}

function markGooglePayFinalSubmitDispatched(token: string): void {
	lastGooglePayFinalSubmitToken = token;
	lastGooglePayFinalSubmitTs = Date.now();
}

function clearGooglePayFinalSubmitInProgressStateIfNeeded(): void {
	if (!isConvesioPayGooglePayAutoSubmitInProgress) {
		return;
	}

	isConvesioPayGooglePayAutoSubmitInProgress = false;
}

function stopConvesioPayProcessingAfterFailure(): void {
	clearGooglePayFinalSubmitInProgressStateIfNeeded();
	isConvesioPayGooglePayAuthorizationInProgress = false;
	clearGooglePayAuthorizationFocusReset?.();
	clearGooglePayAuthorizationFocusReset = null;
	isConvesioPayOrderSubmissionInProgress = false;
	store.dispatch(stopModalLoading());
}

function startConvesioPayGooglePayAuthorizationProcessing(): void {
	isConvesioPayGooglePayAuthorizationInProgress = true;
	store.dispatch(startModalProcessing());

	if (clearGooglePayAuthorizationFocusReset) {
		return;
	}

	const resetIfGooglePayReturnedWithoutToken = (): void => {
		window.setTimeout(() => {
			if (!isConvesioPayGooglePayAuthorizationInProgress || getGooglePayFinalSubmitTokenSnapshot().token) {
				return;
			}

			stopConvesioPayProcessingAfterFailure();
		}, 150);
	};

	window.addEventListener('focus', resetIfGooglePayReturnedWithoutToken);
	clearGooglePayAuthorizationFocusReset = () => {
		window.removeEventListener('focus', resetIfGooglePayReturnedWithoutToken);
	};
}

function clearConvesioPayGooglePayPaymentState(): void {
	const googlePayPaymentData = (window as any).convesiopayGooglePayPaymentData;
	const googlePayToken = getConvesioPayPaymentDataToken(googlePayPaymentData);
	if (googlePayToken && (window as any).convesiopayPaymentToken === googlePayToken) {
		delete (window as any).convesiopayPaymentToken;
	}

	delete (window as any).convesiopayGooglePayPaymentData;
}

function validateConvesioPayGooglePayPolicies(): boolean {
	if (validateRequiredPolicyCheckboxes()) {
		return true;
	}

	displayPaymentErrorMessage(requiredPolicyErrorMessage);
	clearConvesioPayGooglePayPaymentState();
	stopConvesioPayProcessingAfterFailure();
	return false;
}

function validateConvesioPayApplePayPolicies(): boolean {
	if (validateRequiredPolicyCheckboxes()) {
		return true;
	}

	displayPaymentErrorMessage(requiredPolicyErrorMessage);
	clearConvesioPayApplePayPaymentState();
	stopConvesioPayProcessingAfterFailure();
	return false;
}

function clearConvesioPaySubmitInProgressState(): void {
	clearGooglePayFinalSubmitInProgressStateIfNeeded();
	isConvesioPayOrderSubmissionInProgress = false;
}

function redirectToConvesioPayOrderResult(redirectURL: string): void {
	let didStartRedirect = false;
	const fallbackTimer = window.setTimeout(() => {
		clearConvesioPaySubmitInProgressState();
	}, CONVESIOPAY_REDIRECT_FALLBACK_RESET_MS);

	try {
		(window.top ?? window).location.href = redirectURL;
		didStartRedirect = true;
	} catch {
		// Sandboxed or embedded contexts can reject top-level navigation.
	} finally {
		if (!didStartRedirect) {
			window.clearTimeout(fallbackTimer);
			clearConvesioPaySubmitInProgressState();
		}
	}
}

function displayConvesioPayGooglePayErrorMessage(error: string): void {
	displayPaymentErrorMessage(error);
	document.querySelectorAll<HTMLElement>('.pp-pm-error-text').forEach($el => {
		$el.dataset['convesiopayGooglePayError'] = 'true';
	});
}

function clearConvesioPayGooglePayFailureState(): void {
	document.querySelectorAll(CONVESIOPAY_GOOGLE_PAY_ERROR_SELECTOR).forEach($el => {
		$el.remove();
	});

	clearConvesioPayGooglePayPaymentState();
}

function convesioPayOrderErrorMessage(error: unknown): string {
	return getErrorMessage(error).trim() || getLocaleText('An unknown error occured while placing the order. Please refresh the page and try again.');
}

async function continueGooglePaySubmitAfterRecalculate(recalculatePromise?: Promise<void> | null): Promise<void> {
	const startState = getGooglePayFinalSubmitTokenSnapshot();

	if (!startState.token) {
		return;
	}

	if (!validateConvesioPayGooglePayPolicies()) {
		return;
	}

	if (isGooglePayFinalSubmitDuplicate(startState.token)) {
		return;
	}

	if (!canAutoSubmitGooglePay()) {
		return;
	}

	markGooglePayAutoSubmitLocked();

	try {
		if (recalculatePromise) {
			await recalculatePromise.catch(() => undefined);
		}

		const finalTokenState = getGooglePayFinalSubmitTokenSnapshot();
		if (!finalTokenState.token) {
			markGooglePayFinalSubmitDispatched(`googlepay_blocked_${Date.now()}`);
			clearGooglePayFinalSubmitInProgressStateIfNeeded();
			return;
		}

		const googlePayPaymentData = (window as any).convesiopayGooglePayPaymentData;
		if (isConvesioPayGooglePayPaymentDataStale(googlePayPaymentData)) {
			markGooglePayFinalSubmitDispatched(`googlepay_stale_${Date.now()}`);
			await invalidateStaleConvesioPayGooglePayPaymentData(getLocaleText('The order total changed. Please authorize Google Pay again.'), false);
			return;
		}

		markGooglePayFinalSubmitDispatched(finalTokenState.token);

		if (!convesioPayFinalSubmitHandler) {
			clearGooglePayFinalSubmitInProgressStateIfNeeded();
			return;
		}

		await convesioPayFinalSubmitHandler();
	} catch (error) {
		clearGooglePayFinalSubmitInProgressStateIfNeeded();
	}
}

async function createGooglePaySessionWithRetry(params: {
	integration?: string;
	returnUrl: string;
	orderNumber: string;
	amount: number;
	currency: string;
	email: string;
	name: string;
}, options?: {maxAttempts?: number; delayMs?: number}): Promise<boolean> {
	const maxAttempts = Number(options?.maxAttempts ?? 3);
	const delayMs = Number(options?.delayMs ?? 450);
	let attempt = 0;

	const attemptCreate = async (): Promise<boolean> => {
		try {
			await mountedConvesioPayComponent.createGooglePaySession(params);

			return true;
		} catch (error) {
			const isRecoverable = isGooglePaySessionRecoverableError(error);

			if (isRecoverable) {
				return false;
			}

			if (!isGooglePaySessionNotReadyError(error) || attempt >= (maxAttempts - 1)) {
				throw error instanceof Error ? error : new Error(String(error));
			}

			attempt += 1;
			await new Promise<void>(resolve => {
				setTimeout(resolve, delayMs);
			});
			return attemptCreate();
		}
	};

	const isSessionCreated = await attemptCreate();
	return isSessionCreated;
}

async function ensureGooglePaySessionReady(): Promise<void> {
	if (!mountedConvesioPayComponent || !isGooglePayMethodEnabled()) {
		return;
	}

	if (Date.now() < googlePaySessionRetryLockUntilMs) {
		return;
	}

	if (googlePaySessionCreationPromise) {
		await googlePaySessionCreationPromise;
		return;
	}

	const orderData = await getOrderDataForConvesioPay();
	const signature = getGooglePaySessionSignature(orderData);
	const expectedSignature = signature;

	if (googlePaySessionPrimed && googlePaySessionSignature === expectedSignature) {
		return;
	}

	const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
	const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? settings.integration_name ?? window.location.hostname;

	const create = async () => {
		const isSessionCreated = await createGooglePaySessionWithRetry({
			integration: integrationName,
			returnUrl: window.location.href,
			orderNumber: orderData.orderNumber,
			amount: orderData.amount,
			currency: orderData.currency,
			email: orderData.email,
			name: orderData.name,
		}, {
			maxAttempts: 3,
			delayMs: 450,
		});

		if (!isSessionCreated) {
			throw new Error('Google Pay session creation failed');
		}

		googlePaySessionPrimed = true;
		googlePaySessionSignature = expectedSignature;
	};

	googlePaySessionCreationPromise = create()
		.catch(error => {
			googlePaySessionPrimed = false;
			googlePaySessionSignature = '';
			googlePaySessionRetryLockUntilMs = Date.now() + 3000;
			clearConvesioPayGooglePayPaymentState();
			throw error instanceof Error ? error : new Error(String(error));
		})
		.finally(() => {
			googlePaySessionCreationPromise = null;
		});
	await googlePaySessionCreationPromise;

	// Optional lightweight marker for diagnostics without adding noisy logs.
	(window as any).convesiopayGooglePaySessionSignature = googlePaySessionSignature;
}

/**
 * Get fee configuration from Feature metadata or window object
 */
function getFeeConfig(): {feeConfig: FeeConfig; cartTotal: number} {
	// Try Feature.metadata first
	const feeConfig = Feature.metadata<FeeConfig>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'fee_config') ?? {};
	const cartTotal = Feature.metadata<number>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'cart_total') ?? 0;

	// Fallback to window object
	if (Object.keys(feeConfig).length === 0) {
		const windowData = (window as any).peachpay_convesiopay_unified_data ?? {};
		return {
			feeConfig: windowData.fee_config ?? {},
			cartTotal: parseFloat(windowData.cart_total ?? '0') || 0,
		};
	}

	return {feeConfig, cartTotal};
}

function doesConvesioPayMethodAffectTotals(method: ConvesioPayMethod): boolean {
	const {feeConfig} = getFeeConfig();
	const methodConfig = (feeConfig as Record<string, FeeMethodConfig | undefined>)[method] ?? feeConfig.card;

	if (!methodConfig?.enabled) {
		return false;
	}

	return Number(methodConfig.amount || 0) !== 0;
}

/**
 * Update the fee display in Express Checkout summary.
 * This directly manipulates the DOM to show the fee for the selected payment method.
 *
 * @param method - The selected payment method (card, applepay, googlepay, btcpay)
 */
// eslint-disable-next-line complexity
export function updateFeeDisplay(method: ConvesioPayMethod): void {
	const {feeConfig, cartTotal} = getFeeConfig();
	const methodConfig = (feeConfig as Record<string, FeeMethodConfig | undefined>)[method] ?? feeConfig.card;

	if (!methodConfig) {
		return;
	}

	// Calculate fee amount
	let feeAmount = 0;
	if (methodConfig.enabled) {
		const configAmount = methodConfig.amount || 0;
		if (methodConfig.type === 'percent' || methodConfig.type === 'percentage') {
			feeAmount = cartTotal * (configAmount / 100);
		} else {
			feeAmount = configAmount;
		}
	}

	const feeLabel = methodConfig.label || 'Payment gateway fee';
	const convesioPayFeeLabels = getConvesioPayFeeLabels(feeConfig);

	// Find fee display in Express Checkout summary
	// Express Checkout uses .cart-summary-list li.summary-line elements
	const summaryLists = document.querySelectorAll('.cart-summary-list');

	for (const summaryList of Array.from(summaryLists)) {
		const allLines = Array.from(summaryList.querySelectorAll('li.summary-line'));

		const convesioPayFeeLines: HTMLElement[] = [];

		for (const lineElement of allLines) {
			const line = lineElement as HTMLElement;
			const isCoupon = isCouponSummaryLine(line);
			const isShipping = isShippingSummaryLine(line);

			if (isCoupon || isShipping) {
				line.removeAttribute('data-fee-type');
				line.style.display = '';
				continue;
			}

			if (!isConvesioPayOwnedFeeSummaryLine(line, convesioPayFeeLabels)) {
				line.removeAttribute('data-fee-type');
				line.style.display = '';
				continue;
			}

			line.dataset['feeType'] = 'convesiopay';

			convesioPayFeeLines.push(line);
		}

		let feeLine = convesioPayFeeLines[0];

		// Remove duplicate ConvesioPay fee lines. Never remove normal cart summary rows.
		for (const line of convesioPayFeeLines) {
			if (line !== feeLine) {
				line.remove();
			}
		}

		if (feeAmount > 0) {
			if (!feeLine) {
				// Create new fee line - insert before the Total line
				let insertBeforeElement: Element | undefined;

				// Find the Total line (usually last)
				const lastLine = allLines[allLines.length - 1];
				if (lastLine) {
					const previousSibling = lastLine.previousElementSibling;
					insertBeforeElement = (previousSibling?.tagName === 'HR') ? previousSibling : lastLine;
				}

				feeLine = document.createElement('li');
				feeLine.className = 'summary-line';
				feeLine.dataset['feeType'] = 'convesiopay';

				if (insertBeforeElement) {
					insertBeforeElement.before(feeLine);
				} else {
					summaryList.append(feeLine);
				}
			}

			// Update fee line content
			feeLine.innerHTML = `
				<div>Fee - (${feeLabel})</div>
				<div class="pp-recalculate-blur">$${feeAmount.toFixed(2)}</div>
			`;
			feeLine.dataset['rawCost'] = feeAmount.toString();
			feeLine.style.display = '';
		} else if (feeLine) {
			// Hide fee line if no fee
			feeLine.style.display = 'none';
		}
	}

	// Note: Total update happens via cart recalculation when requestCartCalculation() is called
}

function isCouponSummaryLine(line: HTMLElement): boolean {
	if (line.dataset['summaryLineType'] === 'coupon') {
		return true;
	}

	return Boolean(line.querySelector('.pp-coupon-remove-button'));
}

function isShippingSummaryLine(line: HTMLElement): boolean {
	const label = line.querySelector('div')?.textContent?.trim().toLowerCase() ?? '';
	const shippingLabel = getLocaleText('Shipping').trim().toLowerCase();

	return label === 'shipping' || label === shippingLabel;
}

function isConvesioPayFeeSummaryLine(line: HTMLElement, feeLabel?: string): boolean {
	const label = line.querySelector('div')?.textContent?.trim().toLowerCase() ?? '';

	if (!label.startsWith('fee -')) {
		return false;
	}

	return feeLabel === undefined || label === `fee - (${feeLabel})`.toLowerCase();
}

function getConvesioPayFeeLabels(feeConfig: FeeConfig): Set<string> {
	const labels = new Set<string>();

	for (const methodConfig of Object.values(feeConfig)) {
		if (!methodConfig) {
			continue;
		}

		labels.add(normalizeConvesioPayFeeLabel(methodConfig.label || 'Payment gateway fee'));
	}

	return labels;
}

function isConvesioPayOwnedFeeSummaryLine(line: HTMLElement, feeLabels: Set<string>): boolean {
	// Already explicitly marked — trust the attribute, skip label inference.
	if (line.dataset['feeType'] === 'convesiopay') {
		return isConvesioPayFeeSummaryLine(line);
	}

	// First encounter: verify via label, then immediately mark the row so future calls
	// use the safe attribute path and never re-match an unrelated merchant fee row.
	const summaryFeeLabel = getSummaryFeeLabel(line);

	if (summaryFeeLabel !== undefined && feeLabels.has(summaryFeeLabel)) {
		line.dataset['feeType'] = 'convesiopay';
		return true;
	}

	return false;
}

function getSummaryFeeLabel(line: HTMLElement): string | undefined {
	const label = line.querySelector('div')?.textContent?.trim() ?? '';
	const match = /^fee - \((.*)\)$/i.exec(label);

	return match?.[1] ? normalizeConvesioPayFeeLabel(match[1]) : undefined;
}

function normalizeConvesioPayFeeLabel(label: string): string {
	return label.trim().toLowerCase();
}

/**
 * Update the fee display when payment method changes.
 * This is called when the ConvesioPay component fires a 'change' event with a method type.
 */
function shouldRecalculateCartForConvesioPayMethodChange(
	previousMethod: ConvesioPayMethod | undefined,
	method: ConvesioPayMethod,
): boolean {
	// First selection only needs a server round-trip when the method can add fees.
	if (previousMethod === undefined) {
		return doesConvesioPayMethodAffectTotals(method);
	}

	// Any later switch must sync WC session (selected method + fee removal/application).
	return true;
}

async function updateConvesioPayMethodAndFee(method: ConvesioPayMethod): Promise<void> {
	if (method === currentConvesioPayMethod) {
		if (method !== 'googlepay') {
			clearConvesioPayGooglePayFailureState();
		}

		return Promise.resolve();
	}

	const previousMethod = currentConvesioPayMethod;
	currentConvesioPayMethod = method;

	if (method !== 'googlepay') {
		clearConvesioPayGooglePayFailureState();
	}

	if (method !== 'applepay') {
		clearConvesioPayApplePayPaymentState();
	}

	if (method !== 'card') {
		clearConvesioPayCardToken();
	}

	// Store in window for reference (used by order.ts for cart calculation)
	(window as unknown as Record<string, unknown>)['convesiopaySelectedMethod'] = method;

	// Update the fee display in the DOM directly (click handler approach)
	updateFeeDisplay(method);

	if (!shouldRecalculateCartForConvesioPayMethodChange(previousMethod, method)) {
		return Promise.resolve();
	}

	// CRITICAL: If switching to Apple Pay, recreate the session with correct amount
	// The session created at mount time has the wrong fee (Card fee instead of Apple Pay fee)
	if (method === 'applepay' && mountedConvesioPayComponent) {
		void recreateApplePaySessionWithCorrectAmount();
	}

	// Also trigger cart recalculation to update server-side fees and totals
	const calculationPromise = requestCartCalculation('push', 'convesiopay:method-change', {cartCalculationDedupeKey: 'first-open'});

	if (method === 'googlepay' && mountedConvesioPayComponent) {
		return calculationPromise.then(async () => recreateGooglePaySessionWithCorrectAmount());
	}

	return calculationPromise;
}

/**
 * Calculate the correct total for a specific payment method (Express Checkout)
 * Uses base cart total + the correct fee for the method
 */
function calculateCorrectTotalForMethod(method: ConvesioPayMethod): number {
	const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
	const feeConfig = settings.fee_config ?? {};

	// Get base cart total from live store (includes coupons, shipping, tax)
	// Prioritize live store value over stale PHP-provided value
	let baseCartTotal = DefaultCart.total();

	// Fallback to PHP-provided value only if store is empty
	if (baseCartTotal <= 0) {
		baseCartTotal = parseFloat(settings.cart_total ?? 0) || 0;
	}

	// Get fee config for the method
	const methodConfig = feeConfig[method] ?? {};

	// Calculate fee
	let feeAmount = 0;
	if (methodConfig.enabled) {
		const feePercent = parseFloat(methodConfig.amount ?? 0) || 0;
		if (methodConfig.type === 'percent' || methodConfig.type === 'percentage') {
			feeAmount = baseCartTotal * (feePercent / 100);
		} else {
			feeAmount = feePercent;
		}

		feeAmount = Math.round((feeAmount + 0.00000001) * 100) / 100;
	}

	// Return total in cents
	return Math.round((baseCartTotal + feeAmount) * 100);
}

/**
 * Recreate Apple Pay session with the correct amount (including Apple Pay fee)
 */
async function recreateApplePaySessionWithCorrectAmount(): Promise<void> {
	if (!mountedConvesioPayComponent) {
		return;
	}

	try {
		const correctApplePayAmount = calculateCorrectTotalForMethod('applepay');
		const orderData = await getOrderDataForConvesioPay();
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? settings.integration_name;

		await mountedConvesioPayComponent.createApplePaySession({
			integration: integrationName,
			returnUrl: window.location.href,
			amount: correctApplePayAmount,
			currency: orderData.currency,
			email: orderData.email,
			name: orderData.name,
		});

		setApplePaySessionAmountCents(correctApplePayAmount);
	} catch (error) {
		// Apple Pay session recreation failed - will use existing session
	}
}

/**
 * Recreate Google Pay session with the correct amount (including Google Pay fee).
 * Mirrors recreateApplePaySessionWithCorrectAmount for the Google Pay path.
 */
async function recreateGooglePaySessionWithCorrectAmount(): Promise<void> {
	if (!mountedConvesioPayComponent || !isGooglePayMethodEnabled()) {
		return;
	}

	try {
		const orderData = await getOrderDataForConvesioPay();
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? settings.integration_name ?? window.location.hostname;

		// Reset session state so any subsequent ensureGooglePaySessionReady call won't
		// short-circuit on the stale pre-fee signature.
		googlePaySessionPrimed = false;
		googlePaySessionSignature = '';

		const isSessionCreated = await createGooglePaySessionWithRetry({
			integration: integrationName,
			returnUrl: window.location.href,
			orderNumber: orderData.orderNumber,
			amount: orderData.amount,
			currency: orderData.currency,
			email: orderData.email,
			name: orderData.name,
		}, {
			maxAttempts: 3,
			delayMs: 450,
		});

		if (!isSessionCreated) {
			clearConvesioPayGooglePayPaymentState();
			throw new Error(getLocaleText('Google Pay is not ready. Please try again.'));
		}

		// Update the signature with the fee-adjusted amount only after the SDK confirms
		// a fresh session was created for that amount.
		googlePaySessionPrimed = true;
		googlePaySessionSignature = `${orderData.amount}-${orderData.currency}`;
		(window as any).convesiopayGooglePaySessionSignature = googlePaySessionSignature;
	} catch (error) {
		googlePaySessionPrimed = false;
		googlePaySessionSignature = '';
		(window as any).convesiopayGooglePaySessionSignature = googlePaySessionSignature;
		clearConvesioPayGooglePayPaymentState();
		throw error instanceof Error ? error : new Error(String(error));
	}
}

/**
 * Safely get className as a string (handles SVG elements which use SVGAnimatedString)
 */
function getClassName(element: Element): string {
	const cn = element.className;
	if (typeof cn === 'string') {
		return cn;
	}

	if (cn && typeof (cn as any).baseVal === 'string') {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (cn as any).baseVal;
	}

	return '';
}

/**
 * Detect payment method from accordion-header element by checking its parent accordion-item
 * and the text content of the header.
 */
// eslint-disable-next-line complexity
function detectPaymentMethodFromAccordionHeader(accordionHeader: Element): ConvesioPayMethod | undefined {
	// Method 1: Check the parent accordion-item for payment method classes
	const accordionItem = accordionHeader.closest('.accordion-item');
	if (accordionItem) {
		const itemClassName = getClassName(accordionItem);
		const itemOuterHtml = accordionItem.outerHTML.toLowerCase().slice(0, 500);

		if (itemClassName.includes('google-pay') || itemClassName.includes('googlepay') || itemOuterHtml.includes('google-pay') || itemOuterHtml.includes('googlepay')) {
			return 'googlepay';
		}

		// Check class and HTML for BTCPay indicators
		if (itemClassName.includes('btc-pay') || itemClassName.includes('btcpay') || itemClassName.includes('crypto')
			|| itemOuterHtml.includes('btc-pay') || itemOuterHtml.includes('btcpay')) {
			return 'btcpay';
		}

		// Check for Apple Pay
		if (itemClassName.includes('apple-pay') || itemClassName.includes('applepay')
			|| itemOuterHtml.includes('apple-pay') || itemOuterHtml.includes('applepay')) {
			return 'applepay';
		}

		// Check for Card (but not if it's apple or btc)
		if ((itemClassName.includes('new-card') || itemClassName.includes('card-container') || itemClassName.includes('card'))
			&& !itemClassName.includes('apple') && !itemClassName.includes('btc')) {
			return 'card';
		}
	}

	// Method 2: Check accordion-header's own classes and parent classes
	let current: Element | undefined = accordionHeader;
	let depth = 0;
	while (current && depth < 5) {
		const className = getClassName(current);

		if (className.includes('google-pay') || className.includes('googlepay')) {
			return 'googlepay';
		}

		if (className.includes('btc-pay') || className.includes('btcpay') || className.includes('crypto')) {
			return 'btcpay';
		}

		if (className.includes('apple-pay') || className.includes('applepay')) {
			return 'applepay';
		}

		if ((className.includes('new-card') || className.includes('card-container'))
			&& !className.includes('apple') && !className.includes('btc')) {
			return 'card';
		}

		current = current.parentElement ?? undefined;
		depth++;
	}

	// Method 3: Check text content of the accordion header (most reliable fallback)
	const headerText = (accordionHeader.textContent ?? '').toLowerCase();
	if (headerText.includes('crypto') || headerText.includes('bitcoin') || headerText.includes('btc')) {
		return 'btcpay';
	}

	if (headerText.includes('google') || headerText.includes('gpay')) {
		return 'googlepay';
	}

	if (headerText.includes('apple')) {
		return 'applepay';
	}

	if (headerText.includes('card') || headerText.includes('credit') || headerText.includes('debit') || headerText.includes('pay')) {
		// Only return 'card' if it doesn't mention apple or crypto
		if (!headerText.includes('apple') && !headerText.includes('crypto') && !headerText.includes('bitcoin')) {
			return 'card';
		}
	}

	return undefined;
}

/**
 * Bind click events to accordion headers for fee updates in Express Checkout.
 * Uses event delegation to catch clicks on dynamically rendered elements.
 */
function bindAccordionFeeUpdates(): void {
	// Find the ConvesioPay container in Express Checkout
	const container = document.querySelector('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]')
		?? document.querySelector('[class*="convesiopay"]')
		?? document.querySelector('[class*="adyen-payment"]')
		?? document.querySelector('[class*="adyen-checkout"]');

	if (!container) {
		// Retry after a delay if container not found yet
		setTimeout(bindAccordionFeeUpdates, 1000);
		return;
	}

	// Avoid binding multiple times
	const containerElement = container as HTMLElement;
	if (containerElement.dataset['feeBindingAdded'] === 'true') {
		return;
	}

	containerElement.dataset['feeBindingAdded'] = 'true';

	// Use event delegation to capture clicks on accordion headers
	container.addEventListener('click', (event: Event) => {
		const target = event.target as Element;
		if (!target) {
			return;
		}

		// Find the accordion-header from the clicked element
		const accordionHeader = target.closest('.accordion-header');
		if (!accordionHeader) {
			return;
		}

		// Detect which payment method was clicked from the accordion-header
		const method = detectPaymentMethodFromAccordionHeader(accordionHeader);

		if (method) {
			void updateConvesioPayMethodAndFee(method);
		}
	});
}

function isConvesioPayGooglePayButtonTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		return false;
	}

	return target.closest('.vgs-google-pay, #gpay-button-online-api-id') !== null;
}

async function prepareConvesioPayGooglePayAuthorization(): Promise<void> {
	if (googlePayPreAuthorizationPromise) {
		return googlePayPreAuthorizationPromise;
	}

	currentConvesioPayMethod = 'googlepay';
	(window as unknown as Record<string, unknown>)['convesiopaySelectedMethod'] = 'googlepay';
	clearConvesioPayGooglePayFailureState();
	clearConvesioPayGooglePayPaymentState();

	googlePayPreAuthorizationPromise = requestCartCalculation('push', 'convesiopay:googlepay-preauthorization', {
		cartCalculationDedupeKey: 'googlepay-preauthorization',
		force: true,
	})
		.then(async () => recreateGooglePaySessionWithCorrectAmount())
		.finally(() => {
			googlePayPreAuthorizationPromise = null;
		});

	return googlePayPreAuthorizationPromise;
}

function isConvesioPayGooglePaySessionReadyForTrustedClick(): boolean {
	if (!mountedConvesioPayComponent || !isGooglePayMethodEnabled()) {
		return false;
	}

	if (googlePaySessionCreationPromise || Date.now() < googlePaySessionRetryLockUntilMs) {
		return false;
	}

	const sessionAmount = getGooglePaySessionAmountCents();
	const finalAmount = getConvesioPayGooglePayFinalAmountCents();
	return googlePaySessionPrimed && sessionAmount !== null && (finalAmount <= 0 || sessionAmount === finalAmount);
}

function bindGooglePayPolicyValidationGuard(): void {
	if ((document as any).__convesiopayGooglePayPolicyValidationGuard) {
		return;
	}

	const validateBeforeGooglePayAction = (event: Event): void => {
		if (!isConvesioPayGooglePayButtonTarget(event.target)) {
			return;
		}

		if (!validateConvesioPayGooglePayPolicies()) {
			event.preventDefault();
			event.stopImmediatePropagation();
			return;
		}

		if (event.type === 'pointerdown') {
			if (!isConvesioPayGooglePaySessionReadyForTrustedClick()) {
				void prepareConvesioPayGooglePayAuthorization().catch(() => undefined);
			}

			return;
		}

		if (event.type !== 'click' || isConvesioPayGooglePaySessionReadyForTrustedClick()) {
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();
		startConvesioPayGooglePayAuthorizationProcessing();

		void prepareConvesioPayGooglePayAuthorization()
			.then(() => {
				displayConvesioPayGooglePayErrorMessage(getLocaleText('Google Pay is ready. Please try again.'));
				stopConvesioPayProcessingAfterFailure();
			})
			.catch(error => {
				displayConvesioPayGooglePayErrorMessage(error instanceof Error ? error.message : getLocaleText('Google Pay is not ready. Please try again.'));
				stopConvesioPayProcessingAfterFailure();
			});
	};

	document.addEventListener('pointerdown', validateBeforeGooglePayAction, true);
	document.addEventListener('click', validateBeforeGooglePayAction, true);
	(document as any).__convesiopayGooglePayPolicyValidationGuard = true;
}

async function initConvesioPayPaymentIntegration(orderService: OrderService): Promise<void> {
	try {
		// Register lightweight gateway metadata first so the Express Checkout UI can
		// select ConvesioPay without waiting for SDK/component setup.
		registerConvesioPayGatewayMetadata();

		// Get configuration from metadata when available. This avoids calling admin-only config endpoint
		// in Express Checkout where it can return 401.
		const metadataConfig = getConvesioPayConfigFromMetadata();
		if (metadataConfig) {
			await convesioPayGateway.initialize(metadataConfig);
		} else {
			// Fallback to legacy backend endpoint (kept for compatibility)
			const restUrl = (window as any).peachpayConvesioPay?.restUrl || '/wp-json/peachpay/v1/convesiopay/config';
			const response = await fetch(restUrl);
			const result = await response.json();

			if (result.success) {
				await convesioPayGateway.initialize(result.data);
			}
		}

		// Set up button click handler
		setupConvesioPayButtonClickHandler(orderService);
		registerConvesioPayGatewayMountSubscription();

		if (store.getState().paymentConfiguration.selectedGateway === 'peachpay_convesiopay_unified') {
			await mountConvesioPayComponent();
		}
	} catch (error) {
		// Error initializing ConvesioPay
	}
}

export async function registerConvesioPayGateways(): Promise<void> {
	registerConvesioPayGatewayMetadata();
	registerConvesioPayGatewayMountSubscription();
}

export function registerConvesioPayGatewayMetadata(): void {
	// Get active methods from Feature.metadata (primary source for Express Checkout)
	// Fallback to window.peachpay_convesiopay_unified_data if Feature.metadata is not available
	let activeMethods: string[] = [];

	try {
		activeMethods = Feature.metadata<string[]>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'active_methods') ?? [];
	} catch (error) {
		// Fallback to window data if Feature.metadata fails
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};
		activeMethods = settings.active_methods ?? [];
	}

	// Only register if at least one payment method is active
	if (activeMethods.length === 0) {
		return; // Don't register gateway if no methods are active
	}

	// Register ConvesioPay Unified Gateway with the payment system
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	// Register the unified gateway (handles Apple Pay, Google Pay, BTCPay, and Card)
	gatewayConfigurations['peachpay_convesiopay_unified'] = {
		gatewayId: 'peachpay_convesiopay_unified',
		name: getLocaleText('ConvesioPay'),
		description: '',
		assets: {
			badge: {src: cardBadgeURL},
		},
		browser: true, // Supported in all browsers
		initialized: true, // Will be set to false if initialization fails
	};

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	// Setup the ConvesioPay button display
	setupConvesioPayButton();
}

function registerConvesioPayGatewayMountSubscription(): void {
	if (isConvesioPayGatewayMountSubscriptionRegistered) {
		return;
	}

	isConvesioPayGatewayMountSubscriptionRegistered = true;

	// Track previous gateway to prevent re-mounting on every store update
	let previousSelectedGateway: string | null = store.getState().paymentConfiguration.selectedGateway === 'peachpay_convesiopay_unified'
		? 'peachpay_convesiopay_unified'
		: null;
	let isMounting = false;

	// Listen for when this gateway is selected to mount the component
	store.subscribe(() => {
		const {selectedGateway} = store.getState().paymentConfiguration;

		// Skip if gateway hasn't changed
		if (previousSelectedGateway === selectedGateway) {
			return;
		}

		// If switching away from ConvesioPay, unmount
		if (previousSelectedGateway === 'peachpay_convesiopay_unified' && selectedGateway !== 'peachpay_convesiopay_unified') {
			unmountConvesioPayComponent();
			previousSelectedGateway = selectedGateway;
			return;
		}

		// Only mount if switching TO ConvesioPay and not already mounting
		if (selectedGateway === 'peachpay_convesiopay_unified' && !isMounting) {
			previousSelectedGateway = selectedGateway;
			isMounting = true;

			mountConvesioPayComponent()
				.catch(() => {
					// Failed to mount ConvesioPay component
				})
				.finally(() => {
					isMounting = false;
				});
		} else {
			previousSelectedGateway = selectedGateway;
		}
	});
}

/**
 * Get order data for ConvesioPay (following blocks.js pattern)
 */
// eslint-disable-next-line complexity
async function getOrderDataForConvesioPay() {
	const orderData: {
		orderNumber: string;
		amount: number;
		currency: string;
		email: string;
		name: string;
		billingAddress?: {
			street: string;
			city: string;
			state: string;
			postalCode: string;
			country: string;
		};
		shippingAddress?: {
			street: string;
			city: string;
			state: string;
			postalCode: string;
			country: string;
		};
	} = {
		orderNumber: `SESSION-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
		amount: Math.round(DefaultCart.total() * 100), // Convert to cents
		currency: MerchantConfiguration.currency.configuration().code ?? 'USD',
		email: 'customer@example.com',
		name: 'Customer',
	};

	// Try to get customer data from PeachPay order reducer
	try {
		const formData = PeachPayOrder.formData();

		// Helper to safely get string values from FormData (FormData.get returns string | File | null)
		const getString = (value: FormDataEntryValue | null): string | null =>
			typeof value === 'string' ? value : null;

		const email = getString(formData.get('billing_email')) ?? getString(formData.get('email'));
		const firstName = getString(formData.get('billing_first_name')) ?? getString(formData.get('first_name'));
		const lastName = getString(formData.get('billing_last_name')) ?? getString(formData.get('last_name'));
		const billingStreet = getString(formData.get('billing_address_1')) ?? getString(formData.get('billing_street'));
		const billingCity = getString(formData.get('billing_city'));
		const billingState = getString(formData.get('billing_state'));
		const billingPostalCode = getString(formData.get('billing_postcode')) ?? getString(formData.get('billing_postal_code'));
		const billingCountry = getString(formData.get('billing_country'));
		const shippingStreet = getString(formData.get('shipping_address_1')) ?? getString(formData.get('shipping_street')) ?? billingStreet;
		const shippingCity = getString(formData.get('shipping_city')) ?? billingCity;
		const shippingState = getString(formData.get('shipping_state')) ?? billingState;
		const shippingPostalCode = getString(formData.get('shipping_postcode')) ?? getString(formData.get('shipping_postal_code')) ?? billingPostalCode;
		const shippingCountry = getString(formData.get('shipping_country')) ?? billingCountry;

		if (email) {
			orderData.email = email;
		}

		if (firstName || lastName) {
			orderData.name = `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Customer';
		}

		// Add billing address if available
		if (billingStreet || billingCity || billingState || billingPostalCode || billingCountry) {
			orderData.billingAddress = {
				street: billingStreet ?? '123 Default Street',
				city: billingCity ?? 'Default City',
				state: billingState ?? 'CA',
				postalCode: billingPostalCode ?? '90210',
				country: billingCountry ?? 'US',
			};
		}

		// Add shipping address if available (use billing as fallback)
		if (shippingStreet || shippingCity || shippingState || shippingPostalCode || shippingCountry) {
			orderData.shippingAddress = {
				street: shippingStreet ?? orderData.billingAddress?.street ?? '123 Default Street',
				city: shippingCity ?? orderData.billingAddress?.city ?? 'Default City',
				state: shippingState ?? orderData.billingAddress?.state ?? 'CA',
				postalCode: shippingPostalCode ?? orderData.billingAddress?.postalCode ?? '90210',
				country: shippingCountry ?? orderData.billingAddress?.country ?? 'US',
			};
		}
	} catch (error) {
		// Fallback - try to get from form fields
		const emailField = document.querySelector('input[type="email"]');
		if (emailField && (emailField as HTMLInputElement).value) {
			orderData.email = (emailField as HTMLInputElement).value;
		}
	}

	// Ensure we have default addresses if not provided
	if (!orderData.billingAddress) {
		orderData.billingAddress = {
			street: '123 Default Street',
			city: 'Default City',
			state: 'CA',
			postalCode: '90210',
			country: 'US',
		};
	}

	if (!orderData.shippingAddress) {
		orderData.shippingAddress = {
			street: orderData.billingAddress.street,
			city: orderData.billingAddress.city,
			state: orderData.billingAddress.state,
			postalCode: orderData.billingAddress.postalCode,
			country: orderData.billingAddress.country,
		};
	}

	return orderData;
}

/**
 * Create and mount ConvesioPay component (following blocks.js createAndMountConvesioPayComponent pattern)
 */
async function createAndMountConvesioPayComponent() {
	try {
		// Check if ConvesioPay SDK is available
		if (typeof (window as any).ConvesioPay === 'undefined') {
			// Load ConvesioPay script if not already loaded
			await convesioPayGateway.loadConvesioPayScript();

			// Wait a bit for SDK to initialize
			await new Promise<void>(resolve => {
				setTimeout(() => {
					resolve();
				}, 100);
			});

			if (typeof (window as any).ConvesioPay === 'undefined') {
				throw new Error('ConvesioPay SDK not available after loading');
			}
		}

		// Get ConvesioPay configuration using Feature.metadata (like Stripe does)
		const apiKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_key');
		const clientKey = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'client_key');
		const apiUrl = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'api_url') ?? 'https://api.convesiopay.com';
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name');
		const environment = apiUrl.includes('qa') ? 'test' : 'live';

		if (!apiKey || !clientKey) {
			throw new Error('ConvesioPay API key or Client key not found in config');
		}

		// Get order data for customer email and other details
		const orderData = await getOrderDataForConvesioPay();

		// Create ConvesioPay instance
		const cpay = (window as any).ConvesioPay(apiKey);

		// Create component with all options (following blocks.js pattern)
		const component = cpay.component({
			environment,
			clientKey,
			customerEmail: orderData.email,
			integration: integrationName ?? window.location.hostname,
			theme: 'light',
		});

		// Set up event listeners (following blocks.js pattern)
		// eslint-disable-next-line complexity
		component.on('change', async (event: any) => {
			try {
				// Track method changes for fee calculation (Express Checkout)
				const paymentMethod = normalizeConvesioPayMethod(event.type)
					?? normalizeConvesioPayMethod(event.paymentData?.paymentMethod)
					?? normalizeConvesioPayMethod(event.paymentMethod);

				let tokenFromEvent: string | null = null;
				let recalculatePromise: Promise<void> | null = null;

				if (paymentMethod === 'googlepay') {
					if (!validateConvesioPayGooglePayPolicies()) {
						return;
					}

					currentConvesioPayMethod = 'googlepay';
					(window as unknown as Record<string, unknown>)['convesiopaySelectedMethod'] = 'googlepay';
					tokenFromEvent = getConvesiopayGooglePayTokenFromEvent(event);

					if (tokenFromEvent) {
						startConvesioPayGooglePayAuthorizationProcessing();
						clearGooglePayAuthorizationFocusReset?.();
						clearGooglePayAuthorizationFocusReset = null;
						(window as any).convesiopayPaymentToken = tokenFromEvent;
					}

					const authorizedAmount = event.paymentData?.amount ?? event.amount ?? getGooglePaySessionAmountCents() ?? orderData.amount;

					(window as any).convesiopayGooglePayPaymentData = {
						paymentMethod: 'googlepay',
						token: tokenFromEvent,
						hasToken: Boolean(tokenFromEvent),
						hasPaymentData: Boolean(event.paymentData || event.data || event.paymentId || event.paymentData?.paymentId || event.data?.paymentData),
						amount: authorizedAmount,
						currency: event.paymentData?.currency ?? event.currency ?? null,
						status: event.status ?? (event.isSuccessful ? 'authorized' : event.status),
						paymentId: event.paymentId ?? event.paymentData?.paymentId ?? null,
					};
				}

				if (paymentMethod) {
					if (paymentMethod === 'googlepay' && tokenFromEvent) {
						recalculatePromise = requestCartCalculation('push', 'convesiopay:googlepay-authorized', {
							cartCalculationDedupeKey: 'googlepay-authorized',
							force: true,
						});
					} else {
						recalculatePromise = updateConvesioPayMethodAndFee(paymentMethod);
					}
				}

				if (paymentMethod === 'googlepay' && tokenFromEvent) {
					void continueGooglePaySubmitAfterRecalculate(recalculatePromise);
				}

				if (paymentMethod === 'googlepay' && !tokenFromEvent && isConvesioPayGooglePayAuthorizationInProgress && isConvesioPayGooglePayTerminalEvent(event)) {
					stopConvesioPayProcessingAfterFailure();
				}

				// Handle ApplePay payments with auto-submit when successful
				if (paymentMethod === 'applepay') {
					// Check for errors first - only show error if there are actual errors AND payment is not successful
					// This matches blocks.js pattern exactly: check isSuccessful first, then errors
					if (event.isSuccessful) {
						// ApplePay payment intent was successfully authorized
						void handleApplePayPaymentSuccess(component, event);
					} else if (event.errors && event.errors.length > 0) {
						// Handle ApplePay errors - extract detailed error message
						let errorMessage = 'ApplePay payment failed';

						// Try to extract error message from different possible locations
						if (event.error?.data?.body?.message) {
							errorMessage = event.error.data.body.message;
						} else if (event.error?.message) {
							errorMessage = event.error.message;
						} else if (typeof event.errors[0] === 'string') {
							errorMessage = event.errors[0];
						} else if (event.errors[0]?.message) {
							errorMessage = event.errors[0].message;
						}

						// Include status code if available
						if (event.error?.status) {
							errorMessage = `[${event.error.status}] ${errorMessage}`;
						}

						displayPaymentErrorMessage(errorMessage);
						stopConvesioPayProcessingAfterFailure();
					} else if (event.paymentData || event.paymentId || event.paymentData?.paymentId) {
						// Payment data exists even if isSuccessful isn't explicitly true - treat as success
						// This handles cases where the event structure might be slightly different
						void handleApplePayPaymentSuccess(component, event);
					}

					return;
				}

				// Handle BTCPay payments
				if (paymentMethod === 'btcpay') {
					// BTCPay is handled via postMessage listener
					return;
				}

				// Google Pay uses iframe confirmation flow in ConvesioPay and is handled there.
				if (paymentMethod === 'googlepay') {
					return;
				}

				// Handle card payments (default case - card or no type specified)
				if (event.type === 'card' || !event.type || event.type === '') {
					updateConvesioPayCardStateFromChange(event);

					// Don't process ApplePay checks for card events
					return;
				}

				// Also check for ApplePay by payment method and success flags (only for non-card events)
				// This is a fallback check in case event.type wasn't set correctly
				if (event.isValid === true && event.isSuccessful === true
					&& event.type !== 'card' && event.type !== 'btcpay' && event.type !== 'googlepay') {
					// Try to detect payment method
					if (event.paymentData?.paymentMethod === 'applepay'
						|| event.paymentMethod === 'applepay'
						|| event.type?.includes('applepay')) {
						void handleApplePayPaymentSuccess(component, event);
					}
				}
			} catch (error) {
				// Error handling component change
			}
		});

		return {success: true, component};
	} catch (error) {
		return {
			success: false,
			message: error instanceof Error ? error.message : 'Failed to create component',
		};
	}
}

/**
 * Create BTCPay session via AJAX call (following blocks.js pattern)
 */
async function createBTCPaySession(orderData: Awaited<ReturnType<typeof getOrderDataForConvesioPay>>): Promise<{success: boolean; session?: string; message?: string}> {
	if (!isBtcpayMethodEnabled()) {
		return {success: false, message: 'BTCPay payment method is disabled'};
	}

	try {
		// Get AJAX URL and nonce
		const ajaxURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'admin_ajax_url') ?? '/wp-admin/admin-ajax.php';

		// Get nonce from ConvesioPay feature metadata (preferred), or fallback to global config
		let nonce = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'nonce') ?? '';

		if (!nonce && typeof (window as any).peachpayConvesioPayAjax !== 'undefined') {
			nonce = (window as any).peachpayConvesioPayAjax.nonce ?? '';
		}

		// Get integration_name from Feature.metadata (same as component and ApplePay)
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? window.location.hostname;

		const requestData = {
			action: 'peachpay_convesiopay_create_btcpay_session',
			nonce,
			integration: integrationName,
			returnUrl: window.location.href,
			orderNumber: orderData.orderNumber,
			amount: orderData.amount.toString(),
			currency: orderData.currency,
			email: orderData.email,
			name: orderData.name,
			// Include required address fields for ConvesioPay API
			billingAddress: JSON.stringify(orderData.billingAddress ?? {}),
			shippingAddress: JSON.stringify(orderData.shippingAddress ?? {}),
		};

		const response = await fetch(ajaxURL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams(requestData as any).toString(),
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`HTTP ${response.status}: ${errorText}`);
		}

		const sessionResponse = await response.json();

		if (!sessionResponse.success || !sessionResponse.data?.session) {
			const errorMessage = sessionResponse.message ?? sessionResponse.data?.message ?? 'Failed to create BTCPay session';
			throw new Error(errorMessage);
		}

		return {success: true, session: sessionResponse.data.session};
	} catch (error) {
		return {
			success: false,
			message: error instanceof Error ? error.message : 'Failed to create BTCPay session',
		};
	}
}

const CONVESIOPAY_LOADER_HTML
	= '<div class="convesiopay-custom-loader" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:24px;color:#666;">'
	+ '<div class="convesiopay-loader-spinner" style="width:32px;height:32px;border:3px solid #e0e0e0;border-top-color:#0073aa;border-radius:50%;animation:convesiopay-spin 0.8s linear infinite;"></div>'
	+ '<span style="font-size:14px;">Loading payment form...</span></div>';

function injectConvesioPayLoaderStyles(): void {
	if (!document.getElementById('convesiopay-loader-styles')) {
		const style = document.createElement('style');
		style.id = 'convesiopay-loader-styles';
		style.textContent
			= '@keyframes convesiopay-spin { to { transform: rotate(360deg); } }'
			+ ' div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"].loading-in-progress { min-height: 300px !important; min-width: 100% !important; display: flex !important; align-items: center !important; justify-content: center !important; }';
		document.head.appendChild(style);
	}
}

function canAutoSubmitGooglePay(): boolean {
	if (isConvesioPayGooglePayAutoSubmitInProgress) {
		return false;
	}

	const now = Date.now();
	if (now - lastConvesioPayGooglePayAutoSubmitTs < CONVESIOPAY_GPAY_AUTO_SUBMIT_COOLDOWN_MS) {
		return false;
	}

	return true;
}

function markGooglePayAutoSubmitLocked(): void {
	isConvesioPayGooglePayAutoSubmitInProgress = true;
	lastConvesioPayGooglePayAutoSubmitTs = Date.now();

	// Always unlock shortly after attempting auto-submit to avoid dead-locks.
	window.setTimeout(() => {
		isConvesioPayGooglePayAutoSubmitInProgress = false;
	}, CONVESIOPAY_GPAY_AUTO_SUBMIT_COOLDOWN_MS * 2);
}

async function mountConvesioPayComponent(): Promise<void> {
	// Early return if already mounted
	if (isConvesioPayComponentMounted && mountedConvesioPayComponent) {
		return;
	}

	// Find the correct container (following Stripe pattern)
	const mountSelector = '#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]';
	const container = document.querySelector<HTMLElement>(mountSelector);

	if (!container) {
		return;
	}

	container.classList.add('loading-in-progress');
	container.innerHTML = CONVESIOPAY_LOADER_HTML;
	injectConvesioPayLoaderStyles();

	try {
		// Get order data for payment sessions
		const orderData = await getOrderDataForConvesioPay();

		// Create BTCPay session first (following blocks.js pattern)
		let btcPaySession: string | null = null;
		if (isBtcpayMethodEnabled()) {
			try {
				const sessionResult = await createBTCPaySession(orderData);
				if (sessionResult.success && sessionResult.session) {
					btcPaySession = sessionResult.session;
					// Store session globally so it can be used when placing the order
					(window as any).btcPaySession = btcPaySession;
				}
			} catch (sessionError) {
				// BTCPay session creation error (continuing without session)
			}
		}

		// Create and mount the component (following blocks.js pattern)
		const componentResult = await createAndMountConvesioPayComponent();

		if (!componentResult.success || !componentResult.component) {
			throw new Error(componentResult.message ?? 'Failed to create component');
		}

		const {component} = componentResult;

		// Mount the component (replaces loader)
		await component.mount(mountSelector);
		bindGooglePayPolicyValidationGuard();

		// Update mount tracking variables
		isConvesioPayComponentMounted = true;
		mountedConvesioPayComponent = component;
		mountedComponentTarget = mountSelector;

		// Get settings for integration name (following blocks.js pattern exactly)
		const settings = (window as any).peachpay_convesiopay_unified_data ?? {};

		// Get integration_name from Feature.metadata (preferred) or fallback to settings
		const integrationName = Feature.metadata<string>(FeatureFlag.CONVESIOPAY_GATEWAYS, 'integration_name') ?? settings.integration_name;

		// Create ApplePay session (NEW FLOW - no orderNumber)
		try {
			// Create ApplePay session WITHOUT orderNumber to use new token-based flow
			// This matches the card payment flow where payment is created AFTER order exists
			// Note: billingAddress/shippingAddress are NOT included in ApplePay session creation
			// They are only used for BTCPay intent creation
			const applePaySessionResult = await component.createApplePaySession({
				integration: integrationName,
				returnUrl: window.location.href,
				// orderNumber: REMOVED - new flow creates payment after order exists
				amount: orderData.amount,
				currency: orderData.currency,
				email: orderData.email,
				name: orderData.name,
			});

			// Note: createApplePaySession may return undefined - the SDK manages the session internally
			if (applePaySessionResult !== undefined) {
				// Store session for reference if a value is returned
				(window as any).convesiopayApplePaySession = applePaySessionResult;
			}

			setApplePaySessionAmountCents(orderData.amount);
		} catch (applePayError) {
			// ApplePay session creation failed
		}

		// Create BTCPay intent with session (following blocks.js pattern)
		if (btcPaySession) {
			try {
				const btcPayIntentData = {
					session: btcPaySession,
				};

				await component.createBTCPayIntent(btcPayIntentData);

				// Set up postMessage listener for iframe communication (following blocks.js pattern)
				setupBTCPayMessageListener();
			} catch (btcPayError) {
				// BTCPay intent creation skipped or failed
			}
		}

		// Prime Google Pay session/button so the wallet button renders when Google Pay is active.
		await ensureGooglePaySessionReady();

		// Bind click events to accordion headers for immediate fee updates
		// The component's 'change' event alone is not reliable for accordion clicks
		setTimeout(() => {
			bindAccordionFeeUpdates();
		}, 500); // Wait for component to fully render
	} catch (error) {
		// Error mounting ConvesioPay component
		// Reset mount tracking on error
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
		clearConvesioPayCardToken();
	} finally {
		setTimeout(() => {
			const containers = document.querySelectorAll('div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]');
			containers.forEach(($el: Element) => {
				($el as HTMLElement).classList.remove('loading-in-progress');
			});
		}, 800);
	}
}

/**
 * Unmount ConvesioPay component when switching away from ConvesioPay gateway
 */
function unmountConvesioPayComponent(): void {
	if (!isConvesioPayComponentMounted || !mountedConvesioPayComponent) {
		return;
	}

	try {
		// Try to unmount using ConvesioPay SDK method if available
		if (typeof mountedConvesioPayComponent.unmount === 'function') {
			mountedConvesioPayComponent.unmount();
		}

		// Clear the container
		if (mountedComponentTarget) {
			const containers = document.querySelectorAll('div.pp-pm-saved-option[data-gateway="peachpay_convesiopay_unified"]');
			containers.forEach(($el: Element) => {
				($el as HTMLElement).innerHTML = '';
			});
		}

		// Remove BTCPay message listener if it exists
		if ((window as any).__convesiopayBTCPayMessageListener) {
			window.removeEventListener('message', (window as any).__convesiopayBTCPayMessageListener);
			delete (window as any).__convesiopayBTCPayMessageListener;
		}

		// Reset mount tracking variables
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
		clearConvesioPayCardToken();
		googlePaySessionCreationPromise = null;
		googlePaySessionPrimed = false;
		googlePaySessionSignature = '';
		googlePaySessionRetryLockUntilMs = 0;
		clearConvesioPayGooglePayFailureState();
		clearConvesioPayApplePayPaymentState();
	} catch (error) {
		// Error unmounting component
		// Reset tracking even on error
		isConvesioPayComponentMounted = false;
		mountedConvesioPayComponent = null;
		mountedComponentTarget = null;
		clearConvesioPayCardToken();
		googlePaySessionCreationPromise = null;
		googlePaySessionPrimed = false;
		googlePaySessionSignature = '';
		googlePaySessionRetryLockUntilMs = 0;
		clearConvesioPayGooglePayFailureState();
		clearConvesioPayApplePayPaymentState();
	}
}

/**
 * Setup BTCPay message listener for iframe communication (following blocks.js pattern)
 * Listens for payment confirmations and automatically clicks the pay button
 */
function setupBTCPayMessageListener(): void {
	// Check if listener already exists to avoid duplicates
	if ((window as any).__convesiopayBTCPayMessageListener) {
		return;
	}

	// eslint-disable-next-line complexity
	const handleMessage = (event: MessageEvent) => {
		// Verify the origin is from BTCPay or ConvesioPay
		if (!event.origin.includes('btcpay') && !event.origin.includes('convesiopay')) {
			// Still check the data in case origin is different but message is valid
			if (event.data && (
				(typeof event.data === 'string' && event.data.includes('Processing'))
				|| (typeof event.data === 'object' && event.data.status === 'Processing')
			)) {
				// Process anyway
			} else {
				return;
			}
		}

		try {
			let messageData: any;

			// Handle both string and object message data
			if (typeof event.data === 'string') {
				try {
					messageData = JSON.parse(event.data);
				} catch (e) {
					messageData = {type: event.data};
				}
			} else {
				messageData = event.data;
			}

			// Check for payment status messages - 'Processing' means payment received
			if (messageData.status === 'Processing') {
				// Extract payment details from the message
				const invoiceId = messageData.invoiceId ?? messageData.paymentId ?? messageData.invoice_id;
				const paymentId = invoiceId ?? messageData.paymentId ?? messageData.id;

				if (!invoiceId && !paymentId) {
					return;
				}

				const paymentData = {
					invoiceId: invoiceId ?? paymentId,
					status: 'Processing',
					paymentId: paymentId ?? invoiceId,
					paymentMethod: 'btcpay',
					amount: messageData.amount ?? 0,
					currency: messageData.currency ?? 'USD',
					orderNumber: messageData.orderNumber ?? messageData.order_number ?? '',
				};

				// Store payment data globally for use in button click handler
				// Format: btc-session-{invoiceId} (matching blocks.js pattern)
				const paymentToken = `btc-session-${invoiceId ?? paymentId}`;
				(window as any).convesiopayBTCPayPaymentData = paymentData;
				(window as any).convesiopayPaymentToken = paymentToken;

				// Verify token is set before auto-clicking
				if ((window as any).convesiopayPaymentToken) {
					// Auto-click the pay button after a short delay to ensure token is ready
					setTimeout(() => {
						autoClickConvesioPayButton();
					}, 500);
				}
			}
		} catch (error) {
			// Error processing BTCPay message
		}
	};

	window.addEventListener('message', handleMessage);
	(window as any).__convesiopayBTCPayMessageListener = true;
}

/**
 * Handle ApplePay payment success with auto-submit (NEW TOKEN-BASED FLOW)
 * After user authorizes payment on device, we receive a token (NOT a paymentId)
 * Backend will create the payment using secret key AFTER WC order is created
 */
async function handleApplePayPaymentSuccess(_component: any, event: any): Promise<void> {
	try {
		if (!validateConvesioPayApplePayPolicies()) {
			return;
		}

		store.dispatch(startModalProcessing());
		await requestCartCalculation('push', 'convesiopay:applepay-postauthorization', {
			cartCalculationDedupeKey: 'applepay-postauthorization',
			force: true,
		}).catch(() => undefined);

		if (!validateConvesioPayApplePayPolicies()) {
			return;
		}

		// Get order data after forced recalculation so the token is compared against the final total.
		const orderData = await getOrderDataForConvesioPay();

		// NEW FLOW: Extract token from event (SDK provides this after device authorization)
		// Payment does NOT exist yet - it will be created by backend with secret key
		const paymentToken = getConvesiopayGooglePayTokenFromEvent(event);

		if (!paymentToken) {
			displayPaymentErrorMessage('Apple Pay authorization successful but token is missing. Please try again.');
			stopConvesioPayProcessingAfterFailure();
			return;
		}

		const authorizedAmount = getConvesioPayApplePayAuthorizedAmountCents(event.paymentData)
		?? getConvesioPayApplePayAuthorizedAmountCents(event)
		?? getApplePaySessionAmountCents();

		if (authorizedAmount === null) {
			await invalidateStaleConvesioPayApplePayPaymentData(getLocaleText('The order total changed. Please authorize Apple Pay again.'));
			return;
		}

		// Store ApplePay payment data globally for use in button click handler
		// NOTE: No paymentId yet - that will be created by backend
		const applePayPaymentData = {
			paymentMethod: 'applepay',
			token: paymentToken,
			amount: authorizedAmount,
			currency: event.paymentData?.currency ?? orderData.currency,
			status: 'authorized', // Device authorized, payment will be created by backend
		};

		if (isConvesioPayApplePayPaymentDataStale(applePayPaymentData)) {
			await invalidateStaleConvesioPayApplePayPaymentData(getLocaleText('The order total changed. Please authorize Apple Pay again.'));
			return;
		}

		(window as any).convesiopayApplePayPaymentData = applePayPaymentData;
		(window as any).convesiopayPaymentToken = paymentToken;

		// Auto-click the ConvesioPay payment button after a short delay
		setTimeout(() => {
			autoClickConvesioPayButtonForApplePay();
		}, 500);
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Apple Pay failed';
		displayPaymentErrorMessage(errorMessage);
		stopConvesioPayProcessingAfterFailure();
	}
}

/**
 * Auto-click the ConvesioPay payment button after ApplePay payment confirmation
 */
function autoClickConvesioPayButtonForApplePay(): void {
	// Verify payment token is set before clicking
	const paymentToken = (window as any).convesiopayPaymentToken;
	const applePayPaymentData = (window as any).convesiopayApplePayPaymentData;

	if (!paymentToken && !applePayPaymentData?.token) {
		return;
	}

	if (!applePayPaymentData || applePayPaymentData.paymentMethod !== 'applepay') {
		return;
	}

	// Find the ConvesioPay pay button
	const payButtonElement = document.querySelector('.convesiopay-btn');
	const payButton = payButtonElement as HTMLButtonElement | null;

	if (!payButton) {
		// Retry after a short delay in case button hasn't rendered yet
		setTimeout(() => {
			const retryButtonElement = document.querySelector('.convesiopay-btn');
			const retryButton = retryButtonElement as HTMLButtonElement | null;
			if (retryButton && (paymentToken ?? applePayPaymentData?.token)) {
				retryButton.click();
			}
		}, 200);
		return;
	}

	if (payButton.disabled || payButton.classList.contains('hide')) {
		// Try to enable the button
		payButton.disabled = false;
		payButton.classList.remove('hide');

		// Wait a bit and try again
		setTimeout(() => {
			if (!payButton.disabled && !payButton.classList.contains('hide') && (paymentToken ?? applePayPaymentData?.token)) {
				payButton.click();
			}
		}, 100);
		return;
	}

	// Trigger a click event on the button
	const clickEvent = new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
		view: window,
	});

	payButton.dispatchEvent(clickEvent);

	// Also try direct click as fallback
	setTimeout(() => {
		if (!payButton.disabled && !payButton.classList.contains('hide') && (paymentToken ?? applePayPaymentData?.token)) {
			payButton.click();
		}
	}, 100);
}

/**
 * Auto-click the ConvesioPay payment button after BTCPay payment confirmation
 */
function autoClickConvesioPayButton(): void {
	// Verify payment token is set before clicking
	const paymentToken = (window as any).convesiopayPaymentToken;
	const btcPayPaymentData = (window as any).convesiopayBTCPayPaymentData;

	if (!paymentToken) {
		return;
	}

	if (!btcPayPaymentData || btcPayPaymentData.paymentMethod !== 'btcpay') {
		return;
	}

	// Find the ConvesioPay pay button
	const payButtonElement = document.querySelector('.convesiopay-btn');
	const payButton = payButtonElement as HTMLButtonElement | null;

	if (!payButton) {
		// Retry after a short delay in case button hasn't rendered yet
		setTimeout(() => {
			const retryButtonElement = document.querySelector('.convesiopay-btn');
			const retryButton = retryButtonElement as HTMLButtonElement | null;
			if (retryButton && paymentToken) {
				retryButton.click();
			}
		}, 200);
		return;
	}

	if (payButton.disabled || payButton.classList.contains('hide')) {
		// Try to enable the button
		payButton.disabled = false;
		payButton.classList.remove('hide');

		// Wait a bit and try again
		setTimeout(() => {
			if (!payButton.disabled && !payButton.classList.contains('hide') && paymentToken) {
				payButton.click();
			}
		}, 100);
		return;
	}

	// Trigger a click event on the button
	// This will trigger the existing click handler we set up earlier
	const clickEvent = new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
		view: window,
	});

	payButton.dispatchEvent(clickEvent);

	// Also try direct click as fallback
	setTimeout(() => {
		if (!payButton.disabled && !payButton.classList.contains('hide') && paymentToken) {
			payButton.click();
		}
	}, 100);
}

export async function activateConvesioPayGateways(): Promise<void> {
	// Activate ConvesioPay gateways
}

export async function paymentFlow(_context: IConvesioPayContext, order: any): Promise<PaymentResult> {
	try {
		// Create payment token (like GiveWP)
		const token = await convesioPayGateway.createToken();

		// Create payment request with billing address (like GiveWP)
		const paymentRequest: ConvesioPayPaymentRequest = {
			paymentToken: token,
			amount: order.total,
			orderDetails: {
				orderNumber: order.id,
				email: order.billing.email,
				name: `${order.billing.first_name} ${order.billing.last_name}`,
				returnUrl: window.location.href,
				billingAddress: {
					houseNumberOrName: order.billing.address_1,
					street: order.billing.address_2,
					city: order.billing.city,
					stateOrProvince: order.billing.state,
					postalCode: order.billing.postcode,
					country: order.billing.country,
				},
			},
		};

		// Process payment
		return await convesioPayGateway.createPayment(paymentRequest);
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : 'Payment flow failed',
		};
	}
}

/**
 * Set up ConvesioPay button click handler (following Stripe pattern)
 */
function setupConvesioPayButtonClickHandler(orderService: OrderService): void {
	// eslint-disable-next-line complexity
	const confirm = async (event?: Event): Promise<void> => {
		event?.preventDefault();
		event?.stopPropagation();

		if (isConvesioPayOrderSubmissionInProgress) {
			return;
		}

		isConvesioPayOrderSubmissionInProgress = true;
		let retainSubmitLock = false;

		try {
			const googlePayPaymentData = (window as any).convesiopayGooglePayPaymentData;

			// Get payment token from component or payment data. Google Pay is handled only from its current payment data below.
			let paymentToken: string | undefined;
			const btcPayPaymentData = (window as any).convesiopayBTCPayPaymentData;
			const applePayPaymentData = (window as any).convesiopayApplePayPaymentData;
			const selectedConvesioPayMethod = currentConvesioPayMethod
		?? normalizeConvesioPayMethod((window as unknown as Record<string, unknown>)['convesiopaySelectedMethod']);
			const isBtcpayPaymentSubmit = (selectedConvesioPayMethod === undefined || selectedConvesioPayMethod === 'btcpay')
		&& btcPayPaymentData && btcPayPaymentData.paymentMethod === 'btcpay';
			const isApplePayPaymentSubmit = (selectedConvesioPayMethod === undefined || selectedConvesioPayMethod === 'applepay')
		&& applePayPaymentData && applePayPaymentData.paymentMethod === 'applepay';
			const isGooglePayPaymentSubmit = (selectedConvesioPayMethod === undefined || selectedConvesioPayMethod === 'googlepay')
		&& googlePayPaymentData && googlePayPaymentData.paymentMethod === 'googlepay';

			if (isGooglePayPaymentSubmit) {
				if (!validateConvesioPayGooglePayPolicies()) {
					return;
				}

				store.dispatch(startModalProcessing());
			}

			if (isApplePayPaymentSubmit) {
				if (!validateConvesioPayApplePayPolicies()) {
					return;
				}

				store.dispatch(startModalProcessing());
			}

			if (!isGooglePayPaymentSubmit) {
				const storedPaymentToken = (window as any).convesiopayPaymentToken;
				if (typeof storedPaymentToken === 'string') {
					paymentToken = storedPaymentToken;
				}
			}

			// If BTCPay payment data exists, use it (payment was confirmed via postMessage)
			if (isBtcpayPaymentSubmit) {
			// Ensure payment token is set for BTCPay (should already be set by setupBTCPayMessageListener)
				const invoiceId = btcPayPaymentData.invoiceId ?? btcPayPaymentData.paymentId;
				if (!paymentToken && invoiceId) {
				// Fallback: create token from BTCPay payment data
					paymentToken = `btc-session-${invoiceId}`;
					(window as any).convesiopayPaymentToken = paymentToken;
				}
			}

			// If ApplePay payment data exists, use it (payment was confirmed via ApplePay event)
			if (isApplePayPaymentSubmit) {
				if (isConvesioPayApplePayPaymentDataStale(applePayPaymentData)) {
					await invalidateStaleConvesioPayApplePayPaymentData(getLocaleText('The order total changed. Please authorize Apple Pay again.'));
					return;
				}

				// Use ApplePay token if available (prioritize token from applePayPaymentData)
				if (applePayPaymentData.token) {
					paymentToken = applePayPaymentData.token;
					(window as any).convesiopayPaymentToken = paymentToken;
				}
			}

			if (isGooglePayPaymentSubmit) {
				const googlePayToken = getConvesioPayPaymentDataToken(googlePayPaymentData);

				if (!googlePayToken) {
					displayConvesioPayGooglePayErrorMessage(getLocaleText('Google Pay authorization successful but token is missing. Please try again.'));
					stopConvesioPayProcessingAfterFailure();
					return;
				}

				if (isConvesioPayGooglePayPaymentDataStale(googlePayPaymentData)) {
					await invalidateStaleConvesioPayGooglePayPaymentData(getLocaleText('The order total changed. Please authorize Google Pay again.'), false);
					return;
				}

				paymentToken = googlePayToken;
				(window as any).convesiopayPaymentToken = paymentToken;
			}

			if (isConvesioPayCardTokenRequiredForSubmit(
				selectedConvesioPayMethod,
				Boolean(isBtcpayPaymentSubmit),
				Boolean(isApplePayPaymentSubmit),
				Boolean(isGooglePayPaymentSubmit),
			)) {
				try {
					paymentToken = await ensureConvesioPayCardToken(mountedConvesioPayComponent);
				} catch (error) {
					displayPaymentErrorMessage(error instanceof Error ? getLocaleText(error.message) : getLocaleText('Please complete all required payment fields'));
					return;
				}
			}

			// Special handling for BTCPay and ApplePay payments - if we have confirmed payment data,
			// we know payment is confirmed and can proceed even if token format is different
			if (!paymentToken) {
			// Check for BTCPay payment confirmation
				if (isBtcpayPaymentSubmit && btcPayPaymentData.status === 'Processing') {
				// BTCPay payment confirmed but token missing - create it
					const invoiceId = btcPayPaymentData.invoiceId ?? btcPayPaymentData.paymentId;
					if (invoiceId) {
						paymentToken = `btc-session-${invoiceId}`;
						(window as any).convesiopayPaymentToken = paymentToken;
					} else {
						displayPaymentErrorMessage(getLocaleText('Payment confirmation received but payment details are missing. Please try again.'));
						return;
					}
				} else if (isGooglePayPaymentSubmit) {
					const googlePayToken = getConvesioPayPaymentDataToken(googlePayPaymentData);
					if (googlePayToken) {
						paymentToken = googlePayToken;
						(window as any).convesiopayPaymentToken = paymentToken;
					} else {
						displayConvesioPayGooglePayErrorMessage(getLocaleText('Google Pay authorization successful but token is missing. Please try again.'));
						stopConvesioPayProcessingAfterFailure();
						return;
					}
				} else if (isApplePayPaymentSubmit) {
				// Check for ApplePay payment confirmation
				// ApplePay payment confirmed but token missing - use from payment data
					if (applePayPaymentData.token) {
						paymentToken = applePayPaymentData.token;
						(window as any).convesiopayPaymentToken = paymentToken;
					} else {
						displayPaymentErrorMessage(getLocaleText('Payment confirmation received but payment details are missing. Please try again.'));
						return;
					}
				} else {
				// No payment confirmation found
					displayPaymentErrorMessage(getLocaleText('Please complete all required payment fields'));
					return;
				}
			}

			if (typeof paymentToken !== 'string') {
				displayPaymentErrorMessage(getLocaleText('Please complete all required payment fields'));
				return;
			}

			const convesioPayPaymentToken = paymentToken;

			store.dispatch(startModalProcessing());

			// Start transaction
			const {error: transactionError, result: transaction} = await orderService.startTransaction('peachpay_convesiopay_unified')
				.catch(error => ({error, result: undefined}));
			if (transactionError || !transaction) {
				const errorMessage = transactionError ? getErrorMessage(transactionError) : getLocaleText('An unknown error occured while starting the transaction. Please refresh the page and try again.');
				displayPaymentErrorMessage(errorMessage);
				stopConvesioPayProcessingAfterFailure();
				return;
			}

			// Place order with payment token
			// Backend expects 'convesiopay_payment_token' (not 'peachpay_convesiopay_payment_token')
			const extraFormData: Record<string, string> = {
				convesiopay_payment_token: convesioPayPaymentToken,
			};

			// For BTCPay payments, add additional required fields
			if (isBtcpayPaymentSubmit) {
			// Get BTCPay session ID (stored when session was created)
				const btcPaySessionId = (window as any).btcPaySession ?? '';

				// Add BTCPay-specific fields that the backend expects
				if (btcPayPaymentData.invoiceId) {
					extraFormData['btcpay_invoice_id'] = btcPayPaymentData.invoiceId;
					extraFormData['convesiopay_invoice_id'] = btcPayPaymentData.invoiceId;
				}

				if (btcPayPaymentData.paymentId) {
					extraFormData['btcpay_payment_id'] = btcPayPaymentData.paymentId;
					extraFormData['convesiopay_payment_id'] = btcPayPaymentData.paymentId;
				}

				if (btcPayPaymentData.status) {
					extraFormData['btcpay_status'] = btcPayPaymentData.status;
					extraFormData['convesiopay_status'] = btcPayPaymentData.status;
				}

				if (btcPaySessionId) {
					extraFormData['btcpay_session_id'] = btcPaySessionId;
				}
			}

			// For ApplePay payments, add additional required fields (NEW TOKEN-BASED FLOW)
			if (isApplePayPaymentSubmit) {
				if (!validateConvesioPayApplePayPolicies()) {
					return;
				}

				if (isConvesioPayApplePayPaymentDataStale(applePayPaymentData)) {
					await invalidateStaleConvesioPayApplePayPaymentData(getLocaleText('The order total changed. Please authorize Apple Pay again.'));
					return;
				}

				// Add payment_method_type so unified gateway can detect it correctly
				extraFormData['payment_method_type'] = 'applepay';

				// NEW FLOW: Ensure token is explicitly set for ApplePay (backend needs this to create payment)
				// Priority: applePayPaymentData.token > paymentToken > window.convesiopayPaymentToken
				const applePayToken = applePayPaymentData.token
			|| paymentToken
			|| (window as any).convesiopayPaymentToken
			|| '';
				if (applePayToken) {
					extraFormData['convesiopay_payment_token'] = applePayToken;
					paymentToken = applePayToken; // Update paymentToken variable too
				} else {
					displayPaymentErrorMessage(getLocaleText('Apple Pay token is missing. Please try again.'));
					stopConvesioPayProcessingAfterFailure();
					return;
				}

				// NEW FLOW: Send token and order data - backend will create payment with secret key
				// No paymentId at this stage - payment will be created after order exists
				if (applePayPaymentData.amount) {
					extraFormData['applepay_amount'] = applePayPaymentData.amount.toString();
				}

				if (applePayPaymentData.currency) {
					extraFormData['applepay_currency'] = applePayPaymentData.currency;
				}

				if (applePayPaymentData.status) {
					extraFormData['convesiopay_status'] = applePayPaymentData.status;
					extraFormData['status'] = applePayPaymentData.status;
				}

				// Ensure payment method is set correctly
				extraFormData['paymentmethod'] = 'applepay';
			}

			// For Google Pay payments, add additional required fields
			if (isGooglePayPaymentSubmit) {
				if (isConvesioPayGooglePayPaymentDataStale(googlePayPaymentData)) {
					await invalidateStaleConvesioPayGooglePayPaymentData(getLocaleText('The order total changed. Please authorize Google Pay again.'), false);
					return;
				}

				extraFormData['payment_method_type'] = 'googlepay';
				extraFormData['paymentmethod'] = 'googlepay';

				if (googlePayPaymentData.amount) {
					extraFormData['googlepay_amount'] = String(googlePayPaymentData.amount);
				}

				if (googlePayPaymentData.currency) {
					extraFormData['googlepay_currency'] = String(googlePayPaymentData.currency);
				}

				if (googlePayPaymentData.status) {
					extraFormData['convesiopay_status'] = googlePayPaymentData.status;
					extraFormData['status'] = googlePayPaymentData.status;
				}
			}

			// Final validation: Ensure token is in extraFormData before submitting
			if (!extraFormData['convesiopay_payment_token'] || extraFormData['convesiopay_payment_token'].trim() === '') {
				if (isGooglePayPaymentSubmit) {
					displayConvesioPayGooglePayErrorMessage(getLocaleText('Payment token is missing. Please try again.'));
				} else {
					displayPaymentErrorMessage(getLocaleText('Payment token is missing. Please try again.'));
				}

				stopConvesioPayProcessingAfterFailure();
				return;
			}

			const {error: orderError, result: orderResult} = await orderService.placeOrder(transaction, extraFormData)
				.catch(error => ({error, result: undefined}));

			if (orderError || !orderResult || orderResult.result !== 'success') {
				const errorMessage = orderError
					? convesioPayOrderErrorMessage(orderError)
					: orderResult?.result === 'failure'
						? stripHtml(orderResult.messages, null).trim() || getLocaleText('An unknown error occured while placing the order. Please refresh the page and try again.')
						: getLocaleText('An unknown error occured while placing the order. Please refresh the page and try again.');
				if (isGooglePayPaymentSubmit) {
					displayConvesioPayGooglePayErrorMessage(errorMessage);
				} else {
					displayPaymentErrorMessage(errorMessage);
				}

				stopConvesioPayProcessingAfterFailure();
				return;
			}

			// Success - redirect or complete (following Stripe pattern)
			if (orderResult.result === 'success' && 'redirect' in orderResult) {
				const dataURL = new URL(orderResult.redirect);

				if (dataURL.hash === '') {
					await transaction.complete();
					retainSubmitLock = true;
					redirectToConvesioPayOrderResult(dataURL.toString());
				} else {
					await transaction.complete();
					clearConvesioPaySubmitInProgressState();
				}
			}
		} catch (error) {
			retainSubmitLock = false;
			if (isConvesioPayOrderSubmissionInProgress) {
				const errorMessage = error instanceof Error
					? getLocaleText(error.message)
					: getLocaleText('An unknown error occured while placing the order. Please refresh the page and try again.');
				displayPaymentErrorMessage(errorMessage);
			}
		} finally {
			if (!retainSubmitLock && isConvesioPayOrderSubmissionInProgress) {
				stopConvesioPayProcessingAfterFailure();
			}
		}
	};

	convesioPayFinalSubmitHandler = async () => confirm();

	// Use event delegation to handle dynamically shown/hidden buttons
	// This is more reliable than attaching directly since buttons can be hidden/shown dynamically
	const handleClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement;
		const buttonElement = target.closest('.convesiopay-btn');
		const button = buttonElement as HTMLButtonElement | null;
		if (button && !button.classList.contains('hide') && !button.disabled) {
			void confirm(event);
		}
	};

	// Attach to document for event delegation (handles dynamically shown/hidden buttons)
	// Check if handler already exists to avoid duplicates
	if (!(document as any).__convesiopayClickHandler) {
		document.addEventListener('click', handleClick);
		(document as any).__convesiopayClickHandler = true;
	}

	// Also try to attach directly to existing buttons (backup for immediate clicks)
	$qsAll<HTMLButtonElement>('.convesiopay-btn', ($el: HTMLButtonElement) => {
		// Remove existing handler if any to avoid duplicates
		$el.removeEventListener('click', handleClick);
		$el.addEventListener('click', async (event: Event) => {
			event.stopPropagation(); // Prevent delegation handler from also firing
			if (!$el.disabled && !$el.classList.contains('hide')) {
				void confirm(event);
			}
		});
	});
}

// Default export for compatibility
export default async function initConvesioPayPaymentIntegrationWithOrderService(orderService: OrderService): Promise<void> {
	await initConvesioPayPaymentIntegration(orderService);
}
